<script setup>
import { ref, computed, watch, toRef } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import {
  COMPONENT_TYPES,
  findComponentByType,
  buildTemplateViewModel,
} from 'dashboard/helper/templateHelper';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TemplatePreview from 'dashboard/components-next/whatsapp/TemplatePreview.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const emit = defineEmits(['onSelect']);

const { t } = useI18n();
const store = useStore();

const query = ref('');
const categoryFilter = ref('');
const languageFilter = ref('');
const isRefreshing = ref(false);
const selected = ref(null);

// --- Per-browser most-used counter + pins ---
const USAGE_KEY = 'wa_template_usage';
const PIN_KEY = 'wa_template_pins';
const readJson = (key, fallback) => {
  try {
    return JSON.parse(window.localStorage.getItem(key)) ?? fallback;
  } catch (error) {
    return fallback;
  }
};
const writeJson = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // ignore storage errors
  }
};
const usage = ref(readJson(USAGE_KEY, {}));
const pins = ref(readJson(PIN_KEY, []));
const usageCount = name => usage.value[name] || 0;
const usageLabel = name => `${usageCount(name)}×`;
const bumpUsage = name => {
  usage.value = { ...usage.value, [name]: usageCount(name) + 1 };
  writeJson(USAGE_KEY, usage.value);
};
const isPinned = name => pins.value.includes(name);
const togglePin = name => {
  pins.value = isPinned(name)
    ? pins.value.filter(n => n !== name)
    : [...pins.value, name];
  writeJson(PIN_KEY, pins.value);
};

const whatsAppTemplateMessages = useFunctionGetter(
  'inboxes/getFilteredWhatsAppTemplates',
  toRef(props, 'inboxId')
);

const getTemplateBody = template =>
  findComponentByType(template, COMPONENT_TYPES.BODY)?.text || '';

const distinctValues = key => [
  ...new Set(
    whatsAppTemplateMessages.value.map(tpl => tpl[key]).filter(Boolean)
  ),
];
const categories = computed(() => distinctValues('category'));
const languages = computed(() => distinctValues('language'));

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase();
  return whatsAppTemplateMessages.value
    .filter(tpl => {
      if (categoryFilter.value && tpl.category !== categoryFilter.value) {
        return false;
      }
      if (languageFilter.value && tpl.language !== languageFilter.value) {
        return false;
      }
      if (!q) return true;
      return (
        tpl.name.toLowerCase().includes(q) ||
        getTemplateBody(tpl).toLowerCase().includes(q)
      );
    })
    .sort(
      (a, b) =>
        usageCount(b.name) - usageCount(a.name) || a.name.localeCompare(b.name)
    );
});
const pinnedTemplates = computed(() =>
  filtered.value.filter(tpl => isPinned(tpl.name))
);
const otherTemplates = computed(() =>
  filtered.value.filter(tpl => !isPinned(tpl.name))
);

const templateViewModel = template =>
  buildTemplateViewModel({
    definition: template,
    processedParams: {},
    renderedBody: getTemplateBody(template),
    name: template?.name,
  });
const selectedViewModel = computed(() =>
  selected.value ? templateViewModel(selected.value) : null
);
const allLabel = computed(
  () =>
    `${t('WHATSAPP_TEMPLATES.PICKER.ALL_TEMPLATES')} · ${otherTemplates.value.length}`
);

const selectForPreview = template => {
  selected.value = template;
};
const useTemplate = () => {
  if (!selected.value) return;
  bumpUsage(selected.value.name);
  emit('onSelect', selected.value);
};
const toggleCategory = cat => {
  categoryFilter.value = categoryFilter.value === cat ? '' : cat;
};
const toggleLanguage = lang => {
  languageFilter.value = languageFilter.value === lang ? '' : lang;
};

// Keep a valid right-pane selection as the filtered list changes.
watch(
  filtered,
  list => {
    if (!list.length) {
      selected.value = null;
      return;
    }
    if (!selected.value || !list.some(tpl => tpl.id === selected.value.id)) {
      selected.value = list[0];
    }
  },
  { immediate: true }
);

const refreshTemplates = async () => {
  isRefreshing.value = true;
  try {
    await store.dispatch('inboxes/syncTemplates', props.inboxId);
    useAlert(t('WHATSAPP_TEMPLATES.PICKER.REFRESH_SUCCESS'));
  } catch (error) {
    useAlert(t('WHATSAPP_TEMPLATES.PICKER.REFRESH_ERROR'));
  } finally {
    isRefreshing.value = false;
  }
};
</script>

<template>
  <div class="w-full">
    <div class="flex gap-2 mb-2.5">
      <div
        class="flex flex-1 gap-1 items-center px-2.5 py-0 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus-within:outline-n-brand dark:focus-within:outline-n-brand"
      >
        <fluent-icon icon="search" class="text-n-slate-12" size="16" />
        <input
          v-model="query"
          type="search"
          :placeholder="t('WHATSAPP_TEMPLATES.PICKER.SEARCH_PLACEHOLDER')"
          class="reset-base w-full h-9 bg-transparent text-n-slate-12 !text-sm !outline-0"
        />
      </div>
      <button
        :disabled="isRefreshing"
        class="flex justify-center items-center w-9 h-9 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 hover:bg-n-alpha-2 dark:hover:bg-n-solid-2 disabled:opacity-50 disabled:cursor-not-allowed"
        :title="t('WHATSAPP_TEMPLATES.PICKER.REFRESH_BUTTON')"
        @click="refreshTemplates"
      >
        <Icon
          icon="i-lucide-refresh-ccw"
          class="text-n-slate-12 size-4"
          :class="{ 'animate-spin': isRefreshing }"
        />
      </button>
    </div>

    <div
      v-if="categories.length > 1 || languages.length > 1"
      class="flex flex-wrap items-center gap-1.5 mb-2.5"
    >
      <button
        v-for="cat in categories"
        :key="`cat-${cat}`"
        type="button"
        class="px-2.5 py-1 text-xs font-medium rounded-full transition-colors"
        :class="
          categoryFilter === cat
            ? 'bg-n-brand text-white'
            : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
        "
        @click="toggleCategory(cat)"
      >
        {{ cat }}
      </button>
      <span
        v-if="categories.length > 1 && languages.length > 1"
        class="w-px h-4 bg-n-weak"
      />
      <button
        v-for="lang in languages"
        :key="`lang-${lang}`"
        type="button"
        dir="ltr"
        class="px-2.5 py-1 text-xs font-medium rounded-full transition-colors"
        :class="
          languageFilter === lang
            ? 'bg-n-brand text-white'
            : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
        "
        @click="toggleLanguage(lang)"
      >
        {{ lang }}
      </button>
    </div>

    <!-- Panel: two-pane (list | preview) + sticky footer action -->
    <div
      class="flex h-[60vh] flex-col rounded-lg outline outline-1 outline-n-container overflow-hidden bg-n-background"
    >
      <div class="flex flex-1 overflow-hidden">
        <!-- LIST -->
        <div
          class="w-[17rem] shrink-0 overflow-y-auto border-e border-n-weak p-2"
        >
          <div
            v-if="!filtered.length"
            class="py-10 px-2 text-center text-sm text-n-slate-11"
          >
            <template v-if="query || categoryFilter || languageFilter">
              {{ t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_FOUND') }}
            </template>
            <template v-else>
              {{ t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_AVAILABLE') }}
            </template>
          </div>

          <template v-else>
            <template v-if="pinnedTemplates.length">
              <p
                class="flex items-center gap-1 px-1.5 pt-1 pb-1.5 text-[11px] font-semibold text-n-slate-10"
              >
                <Icon icon="i-lucide-pin" class="size-3" />
                {{ t('WHATSAPP_TEMPLATES.PICKER.PINNED') }}
              </p>
              <button
                v-for="tpl in pinnedTemplates"
                :key="`pin-${tpl.id}`"
                type="button"
                class="group flex w-full items-center gap-2 rounded-md border-s-4 px-3 py-3 mb-0.5 text-start transition-colors"
                :class="
                  selected && selected.id === tpl.id
                    ? 'border-n-blue-9 bg-n-blue-9/10'
                    : 'border-transparent hover:bg-n-alpha-2'
                "
                @click="selectForPreview(tpl)"
              >
                <span class="flex-1 truncate text-sm text-n-slate-12" dir="ltr">
                  {{ tpl.name }}
                </span>
                <span
                  v-if="usageCount(tpl.name)"
                  class="text-[10px] tabular-nums text-n-slate-9"
                >
                  {{ usageLabel(tpl.name) }}
                </span>
                <span
                  class="shrink-0 rounded p-0.5 text-n-amber-11 hover:bg-n-alpha-3"
                  :title="t('WHATSAPP_TEMPLATES.PICKER.UNPIN')"
                  @click.stop="togglePin(tpl.name)"
                >
                  <Icon icon="i-lucide-pin" class="size-3.5" />
                </span>
              </button>
            </template>

            <p
              v-if="otherTemplates.length"
              class="px-1.5 pt-2 pb-1.5 text-[11px] font-semibold text-n-slate-10"
            >
              {{ allLabel }}
            </p>
            <button
              v-for="tpl in otherTemplates"
              :key="`tpl-${tpl.id}`"
              type="button"
              class="group flex w-full items-center gap-2 rounded-md border-s-4 px-3 py-3 mb-0.5 text-start transition-colors"
              :class="
                selected && selected.id === tpl.id
                  ? 'border-n-blue-9 bg-n-blue-9/10'
                  : 'border-transparent hover:bg-n-alpha-2'
              "
              @click="selectForPreview(tpl)"
            >
              <span class="flex-1 truncate text-sm text-n-slate-12" dir="ltr">
                {{ tpl.name }}
              </span>
              <span
                v-if="usageCount(tpl.name)"
                class="text-[10px] tabular-nums text-n-slate-9"
              >
                {{ usageLabel(tpl.name) }}
              </span>
              <span
                class="shrink-0 rounded p-0.5 text-n-slate-9 opacity-0 transition-opacity hover:bg-n-alpha-3 group-hover:opacity-100"
                :title="t('WHATSAPP_TEMPLATES.PICKER.PIN')"
                @click.stop="togglePin(tpl.name)"
              >
                <Icon icon="i-lucide-pin" class="size-3.5" />
              </span>
            </button>
          </template>
        </div>

        <!-- PREVIEW -->
        <div class="flex flex-1 flex-col overflow-hidden bg-n-alpha-1">
          <template v-if="selected">
            <div
              class="flex items-center justify-between gap-2 border-b border-n-weak px-4 py-3"
            >
              <p
                class="truncate text-sm font-semibold text-n-slate-12"
                dir="ltr"
              >
                {{ selected.name }}
              </p>
              <div class="flex shrink-0 items-center gap-1.5">
                <span
                  v-if="selected.category"
                  class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] font-medium text-n-slate-11"
                >
                  {{ selected.category }}
                </span>
                <span
                  v-if="selected.language"
                  dir="ltr"
                  class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] font-medium text-n-slate-11"
                >
                  {{ selected.language }}
                </span>
              </div>
            </div>
            <div
              class="flex flex-1 items-center justify-center overflow-y-auto p-5"
            >
              <div
                class="w-full max-w-[22rem] rounded-lg bg-[#dcf8c6] p-3 shadow-md dark:bg-[#005c4b]"
              >
                <TemplatePreview
                  :view-model="selectedViewModel"
                  :show-tag="false"
                />
              </div>
            </div>
          </template>
          <div
            v-else
            class="grid flex-1 place-items-center text-sm text-n-slate-10"
          >
            {{ t('WHATSAPP_TEMPLATES.PICKER.EMPTY_PREVIEW') }}
          </div>
        </div>
      </div>

      <!-- sticky footer action -->
      <div class="border-t border-n-weak bg-n-solid-1 p-4">
        <Button
          :label="t('WHATSAPP_TEMPLATES.PICKER.USE_TEMPLATE')"
          blue
          justify="center"
          icon="i-lucide-send"
          class="w-full"
          :disabled="!selected"
          @click="useTemplate"
        />
      </div>
    </div>
  </div>
</template>
